// The Revise room's worker (`reviseWorker.ts` is this over `self`; tests and a page without workers connect it in
// memory): its own copy of the project's documents, kept up to date by the page (only what changed since it last had
// them), and the two things too slow for the page's thread at a book's length —
//   - History's comparison (`ProjectDiff`): a checkpoint read at its heads, today's draft, and each changed scene diffed
//     word by word. At 150,000 words that's ≈ 750 ms on the page (the checkpoint's 174 scenes read at their heads, and
//     the book's words counted twice); here the page only sends the changes and draws the result.
//   - the style check: the switched-on built-in checks over the manuscript's scenes (≈ 0.8 s at that length).
//   - Revise ▸ Checks' book (`reviseBook`): every mistake, point of typography and consistency set in the manuscript,
//     and every word the spelling dictionary doesn't know (the dictionary fetched once, its checker kept), worked out
//     again after edits.
// One request at a time, in order; the documents are brought up to date between them.
import * as A from '@automerge/automerge';
import {
  checkpointState, compareStates, liveState, openProject, readFragment, readProject, stateFragment, type OpenedProject, type ProjectDiff, type ProjectState, type StateFragment,
} from '@writing-suite/core';
import {
  checkStyle, reviseBook, Speller, styleTexts, type BuiltInStyleCheck, type DashStyle, type ReviseBook, type SpellingDictionary, type StyleFinding,
} from '@writing-suite/core/revise';
import { byPerson, readMembers, wordsAddedBy, type PersonFigure } from '@writing-suite/core/members';
import { checkpointHeadsIn } from '@writing-suite/core';

/** A document the host should hold: `full` replaces its copy, else `bytes` are the changes since the last. */
export interface DocUpdate { name: string; bytes: Uint8Array; full: boolean }

/** Words added by each person (W9c), in a shared project's History. */
export type PersonAdded = PersonFigure & { words: number };
export interface DiffAuthors {
  /** Everyone's words added across the compared scenes, members first, then "Unknown device". */
  total: PersonAdded[];
  /** Per scene, the same. */
  byFragment: [string, PersonAdded[]][];
}

/** History's comparison as it crosses to the page: the diff without its `title` function (the titles instead), and in a
 *  shared project who added the words (`authors`). */
export type SentDiff = Omit<ProjectDiff, 'title'> & { titles: [string, string][]; authors?: DiffAuthors };

/** A comparison with who added its words (shared projects). */
export type AttributedDiff = ProjectDiff & { authors?: DiffAuthors };

export type ReviseRequest =
  | { kind: 'history'; from: string; to: string | null; arrangement: string | null }
  | { kind: 'style'; checks: BuiltInStyleCheck[]; names: string[] }
  | {
    kind: 'book'; styleFindings: StyleFinding[] | null; dismissed: string[]; dashStyle: DashStyle | null;
    /** The spelling check: the project's dictionary and the words the writer knows; null: none. */
    spelling: { dictionary: SpellingDictionary; known: string[] } | null;
  };

/** Checks' book as it crosses: `spelling` says whether the words were checked (false: the dictionary couldn't load). */
export type SentBook = ReviseBook & { spelling: { checked: boolean; error?: string } | null };

/** A dictionary's files (`en.aff` and `<name>.dic`), fetched from the app's own origin. */
export type DictionaryLoader = (name: SpellingDictionary) => Promise<{ aff: string; dic: string }>;

export const fetchDictionary: DictionaryLoader = async (name) => {
  const get = async (file: string) => {
    const r = await fetch(`/dictionaries/${file}`);
    if (!r.ok) throw new Error(`The ${file} dictionary didn’t load (${r.status}).`);
    return r.text();
  };
  const [aff, dic] = await Promise.all([get('en.aff'), get(`${name}.dic`)]);
  return { aff, dic };
};

export type ReviseResponse = SentDiff | StyleFinding[] | SentBook;

export type ToHost =
  | { t: 'sync'; updates: DocUpdate[]; removed: string[] }
  | { t: 'request'; id: number; request: ReviseRequest };

export type FromHost = { t: 'response'; id: number; ok: true; result: ReviseResponse } | { t: 'response'; id: number; ok: false; error: string };

export interface Port<In, Out> {
  post(message: Out, transfer?: Transferable[]): void;
  listen(fn: (message: In) => void): void;
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The diff as it crosses (structured clone keeps its Maps; the function becomes its titles). */
export function sendableDiff(d: ProjectDiff, ids: Iterable<string>): SentDiff {
  const { title, ...rest } = d;
  const titles: [string, string][] = [];
  for (const id of new Set(ids)) titles.push([id, title(id)]);
  return { ...rest, titles };
}

/** The page's `ProjectDiff` again. */
export function receivedDiff(d: SentDiff): AttributedDiff {
  const { titles, ...rest } = d;
  const byId = new Map(titles);
  return { ...rest, title: (id) => byId.get(id) ?? id };
}

export class ReviseHost {
  readonly docs = new Map<string, A.Doc<unknown>>();
  /** Checkpoint states already read (a checkpoint never changes once everything it names has arrived). */
  private checkpoints = new Map<string, ProjectState>();
  /** Today's fragments as read, by document (an unchanged document isn't read again). */
  private today = new WeakMap<A.Doc<unknown>, StateFragment>();
  private queue: Promise<unknown> = Promise.resolve();
  /** The spelling checkers read so far, by dictionary. */
  private spellers = new Map<SpellingDictionary, Promise<Speller>>();

  constructor(private readonly port: Port<ToHost, FromHost>, private readonly loadDictionary: DictionaryLoader = fetchDictionary) {
    port.listen((m) => this.receive(m));
  }

  private receive(m: ToHost) {
    if (m.t === 'sync') {
      for (const name of m.removed) this.docs.delete(name);
      for (const u of m.updates) {
        const had = this.docs.get(u.name);
        this.docs.set(u.name, u.full || !had ? A.load(u.bytes) : A.loadIncremental(had, u.bytes));
      }
      return;
    }
    const run = this.queue.then(() => this.answer(m.request));
    this.queue = run.then(() => {}, () => {});
    void run.then(
      (result) => this.port.post({ t: 'response', id: m.id, ok: true, result }),
      (e) => this.port.post({ t: 'response', id: m.id, ok: false, error: message(e) }),
    );
  }

  answer(r: ReviseRequest): ReviseResponse | Promise<ReviseResponse> {
    return r.kind === 'history' ? this.history(r) : r.kind === 'style' ? this.style(r) : this.book(r);
  }

  private checkpointState(id: string): ProjectState {
    const project = this.docs.get('project');
    if (!project) throw new Error('The project isn’t here yet.');
    const hit = this.checkpoints.get(id);
    if (hit && hit.missingFragments.length === 0) return hit;
    const checkpoint = readProject(project).checkpoints.find((c) => c.id === id);
    if (!checkpoint) throw new Error('That checkpoint isn’t in the project.');
    const state = checkpointState(this.docs, checkpoint);
    this.checkpoints.delete(id);
    this.checkpoints.set(id, state);
    for (const old of this.checkpoints.keys()) if (this.checkpoints.size > 4) this.checkpoints.delete(old);
    return state;
  }

  private todayState(): ProjectState {
    return liveState(this.docs, (id) => {
      const doc = this.docs.get(`fragment.${id}`);
      if (!doc) return null;
      let f = this.today.get(doc);
      if (!f) {
        const r = readFragment(doc);
        f = stateFragment(id, r);
        this.today.set(doc, f);
      }
      return f;
    });
  }

  private history(r: Extract<ReviseRequest, { kind: 'history' }>): SentDiff {
    const a = this.checkpointState(r.from);
    const b = r.to ? this.checkpointState(r.to) : this.todayState();
    const diff = compareStates(a, b, { arrangement: r.arrangement });
    const ids = [...diff.revised.map((x) => x.id), ...diff.structural.flatMap((s) => [s.id, ...(s.type === 'split' ? [s.source] : s.type === 'merged' ? [s.into] : [])])];
    const sent = sendableDiff(diff, ids);
    const authors = this.authors(r, [...diff.revised.map((x) => x.id), ...diff.structural.flatMap((s) => (s.type === 'added' || s.type === 'split' ? [s.id] : []))]);
    return authors ? { ...sent, authors } : sent;
  }

  /** Who wrote the words added in these scenes between the checkpoints (W9c): only in a project with members. */
  private authors(r: Extract<ReviseRequest, { kind: 'history' }>, ids: string[]): DiffAuthors | undefined {
    const project = this.docs.get('project');
    if (!project) return undefined;
    const members = readMembers(project);
    if (!members.list.length) return undefined;
    const checkpoints = readProject(project).checkpoints;
    const from = checkpoints.find((c) => c.id === r.from);
    const to = r.to ? checkpoints.find((c) => c.id === r.to) : null;
    if (!from || to === undefined) return undefined;
    const total = new Map<string, number>();
    const byFragment: [string, PersonAdded[]][] = [];
    for (const id of new Set(ids)) {
      const doc = this.docs.get(`fragment.${id}`);
      if (!doc) continue;
      const fromHex = from.fragmentHeads[id];
      const fromHeads = fromHex ? checkpointHeadsIn(fromHex, doc) : null;
      if (fromHex && !fromHeads) continue; // its history at the checkpoint isn't here (the diff leaves it out too)
      const toHex = to ? to.fragmentHeads[id] : undefined;
      const toHeads = to ? (toHex ? checkpointHeadsIn(toHex, doc) : null) : undefined;
      if (toHeads === null) continue;
      const byActor = wordsAddedBy(doc, fromHeads, toHeads);
      for (const [actor, n] of byActor) total.set(actor, (total.get(actor) ?? 0) + n);
      const people = byPerson(members, byActor);
      if (people.length) byFragment.push([id, people]);
    }
    return { total: byPerson(members, total), byFragment };
  }

  private style(r: Extract<ReviseRequest, { kind: 'style' }>): StyleFinding[] {
    const project = openProject(this.docs) as OpenedProject;
    const texts = styleTexts(project.scenes(), (id) => {
      const f = project.fragmentRecord(id);
      return f ? { title: f.title, text: f.text } : null;
    });
    return checkStyle(texts, r.checks, r.names);
  }

  private async book(r: Extract<ReviseRequest, { kind: 'book' }>): Promise<SentBook> {
    let speller: Speller | null = null;
    let error: string | undefined;
    if (r.spelling) {
      const name = r.spelling.dictionary;
      let loading = this.spellers.get(name);
      if (!loading) {
        loading = this.loadDictionary(name).then(({ aff, dic }) => Speller.fromHunspell(aff, dic));
        this.spellers.set(name, loading);
      }
      try {
        speller = await loading;
      } catch (e) {
        this.spellers.delete(name); // tried again with the next book
        error = message(e);
      }
    }
    const project = openProject(this.docs) as OpenedProject;
    const book = reviseBook(project, {
      styleFindings: r.styleFindings, dismissed: new Set(r.dismissed), ...(r.dashStyle ? { dashStyle: r.dashStyle } : {}),
      ...(speller && r.spelling ? { spelling: { speller, known: r.spelling.known } } : {}),
    });
    return { ...book, spelling: r.spelling ? { checked: speller !== null, ...(error ? { error } : {}) } : null };
  }
}
